import type { HeaderIcon } from "@/types/artifacts.ts";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  Archive,
  ArrowRight,
  Bell,
  Bot,
  Calendar,
  CalendarCheck,
  CalendarPlus,
  Check,
  Clock,
  Download,
  ExternalLink,
  FileText,
  HelpCircle,
  Info,
  LayoutDashboard,
  Link,
  Mail,
  MailOpen,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  PhoneCall,
  Repeat,
  Reply,
  Send,
  Shield,
  Tag,
  ThumbsDown,
  ThumbsUp,
  Trash,
  User,
  UserMinus,
  UserPlus,
  Video,
  XCircle,
  Zap,
} from "lucide-react";
import { Sparkle } from "../icons/customIcons.tsx";

/**
 * Map a server-provided `iconName` (`HeaderIcon`) to its lucide component.
 * Single source of truth for the artifact-icon registry — adding a new
 * artifact type adds one entry here on the client and one entry to
 * `iconForArtifactType()` on the server. Callers that have a full artifact
 * pass `artifact.iconName`; older responses without the field fall through
 * to a neutral placeholder rather than crashing the surface.
 *
 * The icon enum is the same one used by component-level fields (statusBadge,
 * fieldRow, diff, linkButton). One unified vocabulary across chrome + view.
 */
const ICON_BY_NAME: Record<HeaderIcon, LucideIcon> = {
  // Chrome / artifact-type icons
  calendar: Calendar,
  calendarCheck: CalendarCheck,
  calendarPlus: CalendarPlus,
  mail: Mail,
  phone: Phone,
  phoneCall: PhoneCall,
  document: FileText,
  layoutDashboard: LayoutDashboard,
  reply: Reply,
  // Both of the server's names for a message draw the one chat glyph the app
  // uses. Renaming the wire values would strand every installed build that
  // still sends the old ones, so the names stay square and the marks do not.
  messageSquareText: MessageCircle,
  messageSquare: MessageCircle,
  send: Send,
  pencil: Pencil,
  zap: Zap,
  sparkle: Sparkle,
  clock: Clock,
  shield: Shield,
  externalLink: ExternalLink,
  // Status / outcome icons
  check: Check,
  xCircle: XCircle,
  link: Link,
  alertTriangle: AlertTriangle,
  info: Info,
  thumbsUp: ThumbsUp,
  thumbsDown: ThumbsDown,
  helpCircle: HelpCircle,
  // Operation icons
  archive: Archive,
  mailOpen: MailOpen,
  tag: Tag,
  download: Download,
  // Field icons
  mapPin: MapPin,
  video: Video,
  repeat: Repeat,
  bell: Bell,
  arrowRight: ArrowRight,
  userPlus: UserPlus,
  userMinus: UserMinus,
  trash: Trash,
  // Transcript speaker icons
  bot: Bot,
  user: User,
};

export const iconForName = (name: HeaderIcon | undefined): LucideIcon => {
  if (name && ICON_BY_NAME[name]) return ICON_BY_NAME[name];
  return FileText;
};
